import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { useLanguage } from '../../../contexts/LanguageContext';

const CreateAuditModal = ({ isOpen, onClose, onSave, sites }) => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    auditName: '',
    siteId: '',
    auditDate: new Date()?.toISOString()?.split('T')?.[0],
    auditorName: '',
    auditType: 'safety'
  });

  const [errors, setErrors] = useState({});

  const auditTypeOptions = [
    { value: 'safety', label: 'Safety Audit' },
    { value: 'quality', label: 'Quality Inspection' },
    { value: 'environmental', label: 'Environmental Audit' },
    { value: 'regulatory', label: 'Regulatory Compliance' }
  ];

  const siteOptions = sites?.map(site => ({
    value: site?.id,
    label: site?.site_name
  }));

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.auditName?.trim()) {
      newErrors.auditName = 'Audit name is required';
    }

    if (!formData?.siteId) {
      newErrors.siteId = 'Site is required';
    }

    if (!formData?.auditDate) {
      newErrors.auditDate = 'Audit date is required';
    }

    if (!formData?.auditorName?.trim()) {
      newErrors.auditorName = 'Auditor name is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    if (validateForm()) {
      onSave(formData);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-modal flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm">
      <div className="bg-card rounded-lg shadow-elevation-5 w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-4 md:p-6 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-10 h-10 bg-accent/10 rounded-lg">
              <Icon name="ClipboardCheck" size={20} color="var(--color-accent)" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-foreground">{t('createNewAudit')}</h2>
              <p className="text-sm text-muted-foreground">{t('createNewAuditSubtitle')}</p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            aria-label={t('closeModal')}
          >
            <Icon name="X" size={20} />
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-4 md:p-6">
          <div className="space-y-4">
            <Input
              label={t('auditName')}
              type="text"
              placeholder={t('auditNamePlaceholder')}
              value={formData?.auditName}
              onChange={(e) => handleChange('auditName', e?.target?.value)}
              error={errors?.auditName}
              required
            />

            <Select
              label={t('auditType')}
              options={auditTypeOptions}
              value={formData?.auditType}
              onChange={(value) => handleChange('auditType', value)}
              required
            />

            <Select
              label={t('site')}
              options={siteOptions}
              value={formData?.siteId}
              onChange={(value) => handleChange('siteId', value)}
              error={errors?.siteId}
              required
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label={t('auditDate')}
                type="date"
                value={formData?.auditDate}
                onChange={(e) => handleChange('auditDate', e?.target?.value)}
                error={errors?.auditDate}
                required
              />

              <Input
                label={t('auditorName')}
                type="text"
                placeholder={t('enterAuditorName')}
                value={formData?.auditorName}
                onChange={(e) => handleChange('auditorName', e?.target?.value)}
                error={errors?.auditorName}
                required
              />
            </div>

            <div className="bg-accent/10 border border-accent/20 rounded-lg p-4">
              <div className="flex gap-3">
                <Icon name="Info" size={20} color="var(--color-accent)" />
                <div>
                  <p className="text-sm text-foreground font-medium mb-1">{t('auditQuestions')}</p>
                  <p className="text-sm text-muted-foreground">
                    {t('auditQuestionsDesc')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </form>

        <div className="flex items-center justify-end gap-3 p-4 md:p-6 border-t border-border">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
          >
            {t('cancel')}
          </Button>
          <Button
            type="submit"
            onClick={handleSubmit}
            iconName="Plus"
            iconPosition="left"
          >
            {t('createAudit')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CreateAuditModal;